import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, FileText, Trash2, Download, Paperclip, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function POAttachmentsTab({ attachments, setAttachments, notes, setNotes, mandatoryFields, disabled }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  function isRequired(field) {
    return mandatoryFields.includes(field);
  }
  
  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(API + '/upload', formData, {
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const newAttachment = {
        id: Date.now().toString(),
        filename: file.name,
        file_url: response.data.url,
        file_type: file.type,
        uploaded_at: new Date().toISOString()
      };
      
      setAttachments([...attachments, newAttachment]);
      toast.success('File uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }
  
  function removeAttachment(id) {
    setAttachments(attachments.filter(a => a.id !== id));
  }
  
  function getFileIcon(type) {
    if (type.includes('pdf')) return '📄';
    if (type.includes('image')) return '🖼️';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    if (type.includes('word') || type.includes('document')) return '📝';
    return '📎';
  }
  
  return (
    <div className="space-y-6" data-testid="po-attachments-tab">
      {/* File Upload Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Paperclip className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-800">
            Attachments
            {isRequired('attachments') && <span className="text-red-500 ml-1">*</span>}
          </h3>
        </div>
        
        {!disabled && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            />
            <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 mb-2">Upload quotation, invoice, or other documents</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Choose File'}
            </Button>
            <p className="text-xs text-gray-400 mt-2">
              Supported: PDF, Word, Excel, Images (Max 10MB)
            </p>
          </div>
        )}
        
        {/* Attached Files List */}
        {attachments.length > 0 && (
          <div className="mt-4 space-y-2">
            {attachments.map(file => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getFileIcon(file.file_type || '')}</span>
                  <div>
                    <p className="font-medium text-gray-800">{file.filename}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(file.uploaded_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(process.env.REACT_APP_BACKEND_URL + file.file_url, '_blank')}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {!disabled && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(file.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {attachments.length === 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center text-gray-500">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No attachments yet</p>
          </div>
        )}
      </div>
      
      {/* Notes/Remarks Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-800">
            Notes & Remarks
            {isRequired('notes') && <span className="text-red-500 ml-1">*</span>}
          </h3>
        </div>
        
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Add any special instructions, terms, or remarks for this purchase order..."
          className="w-full min-h-[150px] p-3 border rounded-lg resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          disabled={disabled}
          data-testid="po-notes-textarea"
        />
        <p className="text-xs text-gray-400 mt-1">
          {notes.length}/500 characters
        </p>
      </div>
    </div>
  );
}

export default POAttachmentsTab;
